import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import StoryInput from './components/StoryInput';
import StoryTreePanel from './components/StoryTreePanel';
import FullscreenStoryTree from './components/FullscreenStoryTree';
import FullscreenInfiniteCanvas from './components/FullscreenInfiniteCanvas';
import FullscreenInteractiveStory from './components/FullscreenInteractiveStory';
import LanguageSwitcher from './components/LanguageSwitcher';
// Token认证模式 - 认证相关组件
// import AuthModal from './components/AuthModal';
// import UserMenu from './components/UserMenu';
import { storyApi, StoryNode, StoryEdge, StoryBranch } from './services/api';
import logo from './assets/logo.png';

function App() {
  const { t } = useTranslation();
  
  // 滚动到指定区域的函数
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      // 直接滚动到目标区域，使其顶部与页面顶部对齐
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // 延迟聚焦到输入框，确保滚动完成后再聚焦
      setTimeout(() => {
        const textarea = section.querySelector('textarea') as HTMLTextAreaElement;
        if (textarea) {
          textarea.focus();
        }
      }, 500);
    }
  };
  
  // Token认证状态
  // const [user, setUser] = useState<any>(null);
  // const [userProfile, setUserProfile] = useState<any>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Story tree state
  const [nodes, setNodes] = useState<StoryNode[]>([]);
  const [edges, setEdges] = useState<StoryEdge[]>([]);
  const [branches, setBranches] = useState<StoryBranch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreenVisible, setIsFullscreenVisible] = useState(false);
  const [isCanvasVisible, setIsCanvasVisible] = useState(false);
  const [isInteractiveStoryVisible, setIsInteractiveStoryVisible] = useState(false);
  const [branchCount, setBranchCount] = useState(3);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [storyType, setStoryType] = useState('fantasy');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [storyStyle, setStoryStyle] = useState('epic');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canvasStoryContext, setCanvasStoryContext] = useState<any>(null);
  const [interactiveStorySetting, setInteractiveStorySetting] = useState('');

  // Token认证初始化
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsAuthLoading(true);
        setAuthError(null);
        
        // 检查是否已有token
        let hasToken = !!localStorage.getItem('auth_token');
        
        // 如果没有token，尝试获取新token
        if (!hasToken) {
          try {
            const tokenResponse = await storyApi.getToken();
            if (tokenResponse.success && tokenResponse.token) {
              localStorage.setItem('auth_token', tokenResponse.token);
              hasToken = true;
              console.log('New token obtained successfully');
            } else {
              console.warn('Failed to obtain token:', tokenResponse.message);
            }
          } catch (tokenError) {
            console.error('Failed to get token:', tokenError);
          }
        }
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        setAuthError('认证初始化失败，请刷新页面重试');
      } finally {
        setIsAuthLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Token认证处理函数
  const handleAuthRefresh = async () => {
    try {
      setIsAuthLoading(true);
      setAuthError(null);
      
      // 检查是否已有token
      let hasToken = !!localStorage.getItem('auth_token');
      
      // 如果没有token，尝试获取新token
      if (!hasToken) {
        try {
          const tokenResponse = await storyApi.getToken();
          if (tokenResponse.success && tokenResponse.token) {
            localStorage.setItem('auth_token', tokenResponse.token);
            hasToken = true;
            console.log('New token obtained during refresh');
          } else {
            console.warn('Failed to obtain token during refresh:', tokenResponse.message);
          }
        } catch (tokenError) {
          console.error('Failed to get token during refresh:', tokenError);
        }
      }
    } catch (error) {
      console.error('Auth refresh failed:', error);
      setAuthError('认证刷新失败');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleStorySubmit = async (storySetting: string, genre?: string, tone?: string) => {
    setIsLoading(true);
    setError(null);
    // 立即打开故事树，不等待API返回
    setIsFullscreenVisible(true);
    try {
      const response = await storyApi.generateInitialBranches(storySetting, genre, tone, branchCount);
      setNodes(response.nodes);
      setEdges(response.edges);
      setBranches(response.branches);
    } catch (err) {
      setError(t('errors.generateBranches'));
      console.error('Error generating story branches:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateBranches = async (nodeId: string, path?: string[], allNodes?: StoryNode[], allEdges?: StoryEdge[], type?: string, style?: string, count?: number) => {
    setError(null);
    
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      const nodesToUse = allNodes || nodes;
      
      if (allNodes && allNodes.length > nodes.length) {
        const newNodes = allNodes.filter(n => !nodes.some(existing => existing.id === n.id));
        setNodes(prev => [...prev, ...newNodes]);
      }
      
      if (allEdges && allEdges.length > edges.length) {
        const newEdges = allEdges.filter(e => !edges.some(existing => (existing.from === e.from && existing.to === e.to)));
        setEdges(prev => [...prev, ...newEdges]);
      }
      
      const pathContext = path ? path.map(id => {
        const node = nodesToUse.find(n => n.id === id);
        return node ? {
          id: node.id,
          label: node.label,
          content: node.content || node.title || node.label
        } : null;
      }).filter((node): node is NonNullable<typeof node> => node !== null) : [];
      
      const contextString = pathContext.map(node => 
        `${node.label}: ${node.content}`
      ).join(' → ');
      
      const currentBranchCount = count || branchCount;
      console.log('Generating branches based on path:', { nodeId, pathContext, contextString, type, style, count: currentBranchCount });
      
      const response = await storyApi.generateNextBranches(nodeId, contextString, type || storyType, style || storyStyle, currentBranchCount);
      setNodes(prev => {
        const existingIds = new Set(prev.map(n => n.id));
        const newNodes = response.nodes.filter(n => !existingIds.has(n.id));
        return [...prev, ...newNodes];
      });
      setEdges(prev => {
        const existingFromTo = new Set(prev.map(e => `${e.from}->${e.to}`));
        const newEdges = response.edges.filter(e => !existingFromTo.has(`${e.from}->${e.to}`));
        return [...prev, ...newEdges];
      });
      setBranches(prev => {
        const existingIds = new Set(prev.map(b => b.id));
        const newBranches = response.branches.filter(b => !existingIds.has(b.id));
        return [...prev, ...newBranches];
      });
    } catch (err) {
      setError(t('errors.generateNext'));
      console.error('Error generating next branches:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleNodeSave = async (nodeId: string, updatedData: Partial<StoryNode>) => {
    setIsLoading(true);
    try {
      await storyApi.updateNode(nodeId, updatedData);
      setNodes(prev => prev.map(node => 
        node.id === nodeId ? { ...node, ...updatedData } : node
      ));
    } catch (err) {
      setError(t('errors.updateNode'));
      console.error('Error updating node:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleNodeDelete = async (nodeId: string) => {
    if (!window.confirm(t('confirmations.deleteNode'))) {
      return;
    }
    
    setIsLoading(true);
    try {
      await storyApi.deleteNode(nodeId);
      
      const findAllDescendants = (startNodeId: string, allEdges: StoryEdge[]): string[] => {
        const descendants = new Set<string>();
        const queue: string[] = [startNodeId];
        descendants.add(startNodeId);

        while (queue.length > 0) {
          const currentId = queue.shift()!;
          const childEdges = allEdges.filter(edge => edge.from === currentId);
          for (const edge of childEdges) {
            if (!descendants.has(edge.to)) {
              descendants.add(edge.to);
              queue.push(edge.to);
            }
          }
        }
        return Array.from(descendants);
      };
      
      const nodesToDelete = findAllDescendants(nodeId, edges);
      
      setNodes(prev => prev.filter(node => !nodesToDelete.includes(node.id)));
      setEdges(prev => prev.filter(edge => !nodesToDelete.includes(edge.from) && !nodesToDelete.includes(edge.to)));
      setBranches(prev => prev.filter(branch => !nodesToDelete.includes(branch.id)));
      
    } catch (err) {
      setError(t('errors.deleteNode'));
      console.error('Error deleting node:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenFullTree = () => {
    setIsFullscreenVisible(true);
  };

  const handleOpenInfiniteCanvas = () => {
    setIsCanvasVisible(true);
  };
  
  const handleOpenInteractiveStory = (storySetting: string) => {
    setInteractiveStorySetting(storySetting);
    setIsInteractiveStoryVisible(true);
  };

  const handleCanvasStoryGeneration = async (storyContext: any) => {
    // 先打开故事树界面
    setIsFullscreenVisible(true);
    
    // 然后开始loading状态和API调用
    setIsLoading(true);
    setError(null);
    try {
      // 构建基于画布元素的故事设定
      const characterDescriptions = storyContext.characters.map((char: any) => 
        `${char.name}: ${char.description}`
      ).join('; ');
      
      const sceneDescriptions = storyContext.scenes.map((scene: any) => 
        `${scene.name}: ${scene.description}`
      ).join('; ');
      
      const eventDescriptions = storyContext.events.map((event: any) => 
        `${event.name}: ${event.description}`
      ).join('; ');
      
      const worldbuilding = Object.entries(storyContext.worldbuilding || {})
        .map(([key, value]) => `${key}: ${value}`)
        .join('; ');
      
      const storySetting = `角色: ${characterDescriptions}. 场景: ${sceneDescriptions}. ${eventDescriptions ? `事件: ${eventDescriptions}. ` : ''}${worldbuilding ? `世界观: ${worldbuilding}` : ''}`;
      
      const response = await storyApi.generateInitialBranches(storySetting, storyType, storyStyle, branchCount);
      setNodes(response.nodes);
      setEdges(response.edges);
      setBranches(response.branches);
    } catch (err) {
      setError(t('errors.generateBranches'));
      console.error('Error generating story branches from canvas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <div style={{position: 'absolute',top: '20px',right: '20px',display: 'flex',alignItems: 'center',zIndex: 100}}>
        <LanguageSwitcher />
        {/* Token认证状态显示 */}
        {isAuthLoading && (
          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
            认证中...
          </div>
        )}
        {authError && (
          <div style={{ color: '#ff6b6b', fontSize: '14px', cursor: 'pointer' }} onClick={handleAuthRefresh}>
            {authError} (点击重试)
          </div>
        )}
      </div>
      
      <header className="App-header">
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px'}}>
          <img src={logo} alt="logo" style={{width: '50px', height: '50px'}} />
          <h1>{t('app.title')}</h1>
        </div>
        <p>{t('app.subtitle')}</p>
      </header>
      
      <main>
        <div className="main-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* 主页布局分为两部分 */}
          <div className="card section-container story-setting-section">
            <div className="card-title section-title">
              <span role="img" aria-label="setting">🧠</span> {t('app.sections.storySetup')}
            </div>
            <div className="section-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '15px' }}>
              {/* 1. 故事设定部分 */}
              <div className="setup-option feature-card" style={{
                background: 'rgba(67, 97, 238, 0.1)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(67, 97, 238, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={() => {
                // 滚动到页面顶部并聚焦输入框
                scrollToSection('quick-setup-section');
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(67, 97, 238, 0.15)';
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(67, 97, 238, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(67, 97, 238, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div className="card-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#4cc9f0' }}>
                    <span role="img" aria-label="quick">⚡</span> {t('app.setupOptions.quick.title')}
                  </h3>
                  <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', flex: 1 }}>
                    {t('app.setupOptions.quick.description')}
                  </p>
                  <div style={{ textAlign: 'center', width: '100%', marginTop: 'auto' }}>
                    <button className="btn" style={{ background: 'linear-gradient(45deg, #4361ee, #4cc9f0)' }}>
                      {t('app.setupOptions.quick.button')}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="setup-option feature-card" style={{
                background: 'rgba(114, 9, 183, 0.1)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(114, 9, 183, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={handleOpenInfiniteCanvas}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(114, 9, 183, 0.15)';
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(114, 9, 183, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(114, 9, 183, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div className="card-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#f72585' }}>
                    <span role="img" aria-label="detailed">🎨</span> {t('app.setupOptions.detailed.title')}
                  </h3>
                  <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', flex: 1 }}>
                    {t('app.setupOptions.detailed.description')}
                  </p>
                  <div style={{ textAlign: 'center', width: '100%', marginTop: 'auto' }}>
                    <button className="btn" style={{ background: 'linear-gradient(45deg, #7209b7, #f72585)' }}>
                      {t('app.setupOptions.detailed.button')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* 2. 创作模式部分 */}
          <div className="card section-container creation-mode-section">
            <div className="card-title section-title">
              <span role="img" aria-label="create">✨</span> {t('app.sections.creationMode')}
            </div>
            <div className="section-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '15px' }}>
              <div className="creation-option feature-card" style={{
                background: 'rgba(67, 97, 238, 0.1)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(67, 97, 238, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={() => {
                scrollToSection('quick-setup-section');
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(67, 97, 238, 0.15)';
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 5px 15px rgba(67, 97, 238, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(67, 97, 238, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <div className="card-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#4cc9f0' }}>
                    <span role="img" aria-label="tree">🌳</span> {t('app.creationOptions.tree.title')}
                  </h3>
                  <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', flex: 1 }}>
                    {t('app.creationOptions.tree.description')}
                  </p>
                  <div style={{ textAlign: 'center', width: '100%', marginTop: 'auto' }}>
                    <button className="btn" style={{ background: 'linear-gradient(45deg, #4361ee, #4cc9f0)' }}>
                      {t('app.creationOptions.tree.button')}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="creation-option feature-card" 
                  style={{
                    background: 'rgba(114, 9, 183, 0.1)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(114, 9, 183, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }} 
                  onClick={() => {
                     if (nodes.length > 0) {
                       // 如果已有故事数据，可以直接开始交互式故事
                       // 这里可以根据需要调用相应的处理函数
                       scrollToSection('quick-setup-section');
                     } else {
                       scrollToSection('quick-setup-section');
                     }
                   }}
                >
                <div className="card-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#f72585' }}>
                    <span role="img" aria-label="interactive">🎮</span> {t('app.creationOptions.interactive.title')}
                  </h3>
                  <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', flex: 1 }}>
                    {t('app.creationOptions.interactive.description')}
                  </p>
                  <div style={{ textAlign: 'center', width: '100%', marginTop: 'auto' }}>
                    <button className="btn" style={{ background: 'linear-gradient(45deg, #7209b7, #f72585)' }}>
                      {t('app.creationOptions.interactive.button')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* 极速设定表单区域 */}
          <div id="quick-setup-section" className="card">
            <StoryInput
              onSubmit={handleStorySubmit}
              onOpenFullTree={handleOpenFullTree}
              onOpenInfiniteCanvas={handleOpenInfiniteCanvas}
              onOpenInteractiveStory={handleOpenInteractiveStory}
              isLoading={isLoading}
              hasStoryData={nodes.length > 0}
              branchCount={branchCount}
              setBranchCount={setBranchCount}
            />
          </div>
          
          {error && (
            <div className="card status" style={{background: 'rgba(255, 0, 0, 0.08)', color: '#ffb3b3'}}>
              {error}
            </div>
          )}
          
          {nodes.length > 0 && (
            <StoryTreePanel
              nodes={nodes}
              edges={edges}
              branches={branches}
              onNodeSave={handleNodeSave}
              onNodeDelete={handleNodeDelete}
              onGenerateBranches={handleGenerateBranches}
              isLoading={isLoading}
              branchCount={branchCount}
              storyType={storyType}
              storyStyle={storyStyle}
            />
          )}
        </div>
      </main>
      
      <FullscreenStoryTree
        nodes={nodes}
        edges={edges}
        branches={branches}
        isVisible={isFullscreenVisible}
        onClose={() => setIsFullscreenVisible(false)}
        onNodeSave={handleNodeSave}
        onNodeDelete={handleNodeDelete}
        onGenerateBranches={handleGenerateBranches}
        isLoading={isLoading}
        branchCount={branchCount}
        storyType={storyType}
        storyStyle={storyStyle}
      />
      
      <FullscreenInfiniteCanvas
        isVisible={isCanvasVisible}
        onClose={() => setIsCanvasVisible(false)}
        onStoryBranchVisualize={(storyContext) => {
          // 保存画布故事上下文并生成初始故事分支
          setCanvasStoryContext(storyContext);
          handleCanvasStoryGeneration(storyContext);
        }}
      />
      
      {isInteractiveStoryVisible && (
        <FullscreenInteractiveStory
          isVisible={true}
          storySetting={interactiveStorySetting}
          storyType={storyType}
          storyStyle={storyStyle}
          onClose={() => setIsInteractiveStoryVisible(false)}
        />
      )}
      
      {/* Token认证模式 - 暂时移除认证模态框 */}
      {/* 如果需要用户界面，可以在这里添加 */}
      
      {/* 页脚：版权和联系我们 */}
      <footer className="App-footer">
        <div>
          {t('app.footer.copyright', { year: new Date().getFullYear() })}  <a href="mailto:ennn2016@163.com" className="footer-link">{t('app.footer.contact')}</a>
        </div>
      </footer>
    </div>
  );
}

export default App;
